'use client';

import { Component, ErrorInfo, Suspense, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Home,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

interface GlobalErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

export class GlobalErrorBoundary extends Component<
  GlobalErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('Global Error Boundary caught an error:', {
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Store error info in state
    this.setState({
      error,
      errorInfo
    });

    // TODO: Send error to error tracking service (e.g., Sentry)
    // This is where you would integrate with an error tracking service
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    });
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  override render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default error UI
      return (
        <div className="bg-background flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-destructive h-6 w-6" />
                <CardTitle>Oops! Something went wrong</CardTitle>
              </div>
              <CardDescription>
                We encountered an unexpected error. Don't worry, your data is
                safe.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  {this.state.error.message || 'An unexpected error occurred'}
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button onClick={this.handleReset} variant="default">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
                <GoHomeButton />
              </div>

              {/* Error details section */}
              <div className="border-t pt-4">
                <Button
                  onClick={this.toggleDetails}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between"
                >
                  <span className="text-muted-foreground text-sm">
                    {this.state.showDetails ? 'Hide' : 'Show'} Technical Details
                  </span>
                  {this.state.showDetails ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>

                {this.state.showDetails && (
                  <div className="mt-4 space-y-4">
                    <div className="bg-muted overflow-auto rounded-lg p-4 font-mono text-sm">
                      <p className="mb-2 font-semibold">Error:</p>
                      <p className="text-destructive">
                        {this.state.error.toString()}
                      </p>
                    </div>

                    {this.state.error.stack && (
                      <div className="bg-muted max-h-64 overflow-auto rounded-lg p-4 font-mono text-xs">
                        <p className="mb-2 font-semibold">Stack Trace:</p>
                        <pre className="break-words whitespace-pre-wrap">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}

                    {this.state.errorInfo?.componentStack && (
                      <div className="bg-muted max-h-64 overflow-auto rounded-lg p-4 font-mono text-xs">
                        <p className="mb-2 font-semibold">Component Stack:</p>
                        <pre className="break-words whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Small wrapper to use router.push inside the class-based error boundary.
 */
function GoHomeButton() {
  const router = useRouter();
  return (
    <Button onClick={() => router.push('/')} variant="outline">
      <Home className="mr-2 h-4 w-4" />
      Go Home
    </Button>
  );
}

// Hook for using error boundary programmatically
export function useErrorHandler() {
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return {
    throwError: setError,
    clearError: () => setError(null)
  };
}

// Utility component for catching async errors
export function AsyncErrorBoundary({
  children,
  fallback
}: {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}) {
  return (
    <GlobalErrorBoundary fallback={fallback}>
      <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
    </GlobalErrorBoundary>
  );
}
